<template>
  <svg
    :viewBox="`0 0 ${VW} ${VH}`"
    width="100%"
    style="display: block; overflow: visible"
  >
    <defs>
      <!-- Translucent champagne acetate — frame fill -->
      <linearGradient id="acetate" x1="0" y1="0" x2="0.3" y2="1">
        <stop offset="0%" stop-color="rgba(240,230,205,0.82)" />
        <stop offset="35%" stop-color="rgba(220,205,175,0.70)" />
        <stop offset="100%" stop-color="rgba(195,178,145,0.60)" />
      </linearGradient>
      <linearGradient id="acetateEdge" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="rgba(255,248,230,0.95)" />
        <stop offset="30%" stop-color="rgba(215,195,155,0.85)" />
        <stop offset="70%" stop-color="rgba(175,155,120,0.75)" />
        <stop offset="100%" stop-color="rgba(140,120,90,0.9)" />
      </linearGradient>
      <!-- Dark tinted lens -->
      <linearGradient id="lnsDark" x1="0.1" y1="0" x2="0.9" y2="1">
        <stop offset="0%" stop-color="#1e1e1e" />
        <stop offset="100%" stop-color="#080808" />
      </linearGradient>
      <linearGradient id="lnsSheen" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="rgba(255,255,255,0.11)" />
        <stop offset="45%" stop-color="rgba(255,255,255,0.03)" />
        <stop offset="100%" stop-color="rgba(255,255,255,0)" />
      </linearGradient>
      <!-- Hinge metal -->
      <linearGradient id="hingeMetal" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#e8d490" />
        <stop offset="50%" stop-color="#b8922a" />
        <stop offset="100%" stop-color="#8a6a18" />
      </linearGradient>
      <!-- Shadows -->
      <filter id="frameShadow" x="-8%" y="-8%" width="116%" height="140%">
        <feDropShadow
          dx="0"
          dy="12"
          stdDeviation="18"
          flood-color="rgba(0,0,0,0.65)"
          flood-opacity="1"
        />
      </filter>
      <filter id="lensShadow" x="-4%" y="-4%" width="108%" height="116%">
        <feDropShadow
          dx="0"
          dy="4"
          stdDeviation="6"
          flood-color="rgba(0,0,0,0.5)"
        />
      </filter>
      <!-- Arrow markers -->
      <marker
        id="ah"
        markerWidth="7"
        markerHeight="7"
        refX="6"
        refY="3.5"
        orient="auto"
      >
        <path d="M0,1 L6,3.5 L0,6Z" :fill="ac" />
      </marker>
      <marker
        id="at"
        markerWidth="7"
        markerHeight="7"
        refX="1"
        refY="3.5"
        orient="auto"
      >
        <path d="M6,1 L0,3.5 L6,6Z" :fill="ac" />
      </marker>
      <marker
        id="aup"
        markerWidth="7"
        markerHeight="7"
        refX="3.5"
        refY="1"
        orient="auto"
      >
        <path d="M1,6 L3.5,0 L6,6Z" :fill="ac" />
      </marker>
      <marker
        id="adn"
        markerWidth="7"
        markerHeight="7"
        refX="3.5"
        refY="6"
        orient="auto"
      >
        <path d="M1,0 L3.5,7 L6,0Z" :fill="ac" />
      </marker>
    </defs>

    <!-- Temple stubs -->
    <path
      :d="`M ${llx} ${hingeY - fw / 2 + 2} L ${ltip} ${
        hingeY - fw / 2 + 4
      } L ${ltip} ${hingeY + fw / 2 + 4} L ${llx} ${hingeY + fw / 2 + 2} Z`"
      fill="url(#acetate)"
      stroke="url(#acetateEdge)"
      stroke-width="1.5"
    />
    <path
      :d="`M ${rlx + wp} ${hingeY - fw / 2 + 2} L ${rtip} ${
        hingeY - fw / 2 + 4
      } L ${rtip} ${hingeY + fw / 2 + 4} L ${rlx + wp} ${
        hingeY + fw / 2 + 2
      } Z`"
      fill="url(#acetate)"
      stroke="url(#acetateEdge)"
      stroke-width="1.5"
    />

    <!-- LEFT LENS -->
    <rect
      :x="llx + fw"
      :y="lty + fw"
      :width="wp - fw * 2"
      :height="hp - fw * 2"
      :rx="Math.max(rx - fw, 4)"
      fill="url(#lnsDark)"
      filter="url(#lensShadow)"
    />
    <rect
      :x="llx + fw"
      :y="lty + fw"
      :width="wp - fw * 2"
      :height="hp - fw * 2"
      :rx="Math.max(rx - fw, 4)"
      fill="url(#lnsSheen)"
    />
    <ellipse
      :cx="llx + fw + (wp - fw * 2) * 0.22"
      :cy="lty + fw + (hp - fw * 2) * 0.18"
      :rx="(wp - fw * 2) * 0.12"
      :ry="(hp - fw * 2) * 0.06"
      fill="rgba(255,255,255,0.09)"
      :transform="`rotate(-20,${llx + fw + (wp - fw * 2) * 0.22},${
        lty + fw + (hp - fw * 2) * 0.18
      })`"
    />

    <!-- LEFT FRAME -->
    <rect
      :x="llx"
      :y="lty"
      :width="wp"
      :height="hp"
      :rx="rx"
      fill="url(#acetate)"
      stroke="url(#acetateEdge)"
      :stroke-width="fw"
      filter="url(#frameShadow)"
      fill-opacity="0"
    />
    <rect
      :x="llx + 1"
      :y="lty + 1"
      :width="wp - 2"
      :height="hp * 0.4"
      :rx="rx"
      fill="rgba(255,252,240,0.18)"
      stroke="none"
    />

    <!-- RIGHT LENS -->
    <rect
      :x="rlx + fw"
      :y="lty + fw"
      :width="wp - fw * 2"
      :height="hp - fw * 2"
      :rx="Math.max(rx - fw, 4)"
      fill="url(#lnsDark)"
      filter="url(#lensShadow)"
    />
    <rect
      :x="rlx + fw"
      :y="lty + fw"
      :width="wp - fw * 2"
      :height="hp - fw * 2"
      :rx="Math.max(rx - fw, 4)"
      fill="url(#lnsSheen)"
    />
    <ellipse
      :cx="rlx + fw + (wp - fw * 2) * 0.22"
      :cy="lty + fw + (hp - fw * 2) * 0.18"
      :rx="(wp - fw * 2) * 0.12"
      :ry="(hp - fw * 2) * 0.06"
      fill="rgba(255,255,255,0.09)"
      :transform="`rotate(-20,${rlx + fw + (wp - fw * 2) * 0.22},${
        lty + fw + (hp - fw * 2) * 0.18
      })`"
    />

    <!-- RIGHT FRAME -->
    <rect
      :x="rlx"
      :y="lty"
      :width="wp"
      :height="hp"
      :rx="rx"
      fill="url(#acetate)"
      stroke="url(#acetateEdge)"
      :stroke-width="fw"
      filter="url(#frameShadow)"
      fill-opacity="0"
    />
    <rect
      :x="rlx + 1"
      :y="lty + 1"
      :width="wp - 2"
      :height="hp * 0.4"
      :rx="rx"
      fill="rgba(255,252,240,0.18)"
      stroke="none"
    />

    <!-- BRIDGE -->
    <path
      :d="bridgeFullPath"
      fill="url(#acetate)"
      stroke="none"
      opacity="0.95"
    />
    <path
      :d="bridgeOuterPath"
      fill="none"
      stroke="rgba(255,252,238,0.90)"
      stroke-width="1.8"
      stroke-linecap="round"
    />
    <path
      :d="bridgeInnerPath"
      fill="none"
      stroke="rgba(140,115,80,0.55)"
      stroke-width="1"
      stroke-linecap="round"
    />
    <!-- Nose pads -->
    <circle
      :cx="bx1 - 2"
      :cy="bridgeMidY + bridgeWallW * 0.5"
      r="3.5"
      fill="url(#hingeMetal)"
      opacity="0.85"
    />
    <circle
      :cx="bx2 + 2"
      :cy="bridgeMidY + bridgeWallW * 0.5"
      r="3.5"
      fill="url(#hingeMetal)"
      opacity="0.85"
    />

    <!-- HINGES -->
    <rect
      :x="llx - 4"
      :y="hingeY - 9"
      width="13"
      height="18"
      rx="2.5"
      fill="url(#hingeMetal)"
      stroke="rgba(255,235,150,0.3)"
      stroke-width="0.8"
    />
    <circle :cx="llx + 2" :cy="hingeY - 3" r="2" fill="rgba(0,0,0,0.25)" />
    <circle :cx="llx + 2" :cy="hingeY + 4" r="2" fill="rgba(0,0,0,0.25)" />
    <rect
      :x="rlx + wp - 9"
      :y="hingeY - 9"
      width="13"
      height="18"
      rx="2.5"
      fill="url(#hingeMetal)"
      stroke="rgba(255,235,150,0.3)"
      stroke-width="0.8"
    />
    <circle :cx="rlx + wp - 2" :cy="hingeY - 3" r="2" fill="rgba(0,0,0,0.25)" />
    <circle :cx="rlx + wp - 2" :cy="hingeY + 4" r="2" fill="rgba(0,0,0,0.25)" />

    <!-- LENS WIDTH annotation -->
    <g
      @mouseenter="$emit('label-hover', 'lensW')"
      @mouseleave="$emit('label-hover', null)"
      style="cursor: default"
    >
      <line
        :x1="llx"
        :y1="annTopY + 12"
        :x2="llx"
        :y2="lty - 3"
        :stroke="dimLine('lensW')"
        stroke-width="1"
        stroke-dasharray="3 3"
      />
      <line
        :x1="llx + wp"
        :y1="annTopY + 12"
        :x2="llx + wp"
        :y2="lty - 3"
        :stroke="dimLine('lensW')"
        stroke-width="1"
        stroke-dasharray="3 3"
      />
      <line
        :x1="llx"
        :y1="annTopY"
        :x2="llx + wp"
        :y2="annTopY"
        :stroke="bright('lensW')"
        stroke-width="1.6"
        marker-start="url(#at)"
        marker-end="url(#ah)"
      />
      <text
        :x="llx + wp / 2"
        :y="annTopY - 11"
        text-anchor="middle"
        font-size="9.5"
        font-weight="600"
        :fill="bright('lensW')"
        letter-spacing="1.3"
        font-family="HE Manrope, Arial, sans-serif"
      >
        LENS WIDTH
      </text>
      <text
        :x="llx + wp / 2"
        :y="annTopY - 24"
        text-anchor="middle"
        font-size="13"
        font-weight="700"
        fill="rgba(220,185,90,1)"
        letter-spacing="0.5"
        font-family="HE Manrope, Arial, sans-serif"
      >
        {{ r(vals.lensW) }} mm
      </text>
    </g>

    <!-- BRIDGE annotation -->
    <g
      @mouseenter="$emit('label-hover', 'bridge')"
      @mouseleave="$emit('label-hover', null)"
      style="cursor: default"
    >
      <line
        :x1="bx1"
        :y1="annTopY + 12"
        :x2="bx1"
        :y2="lty - 3"
        :stroke="dimLine('bridge')"
        stroke-width="1"
        stroke-dasharray="3 3"
      />
      <line
        :x1="bx2"
        :y1="annTopY + 12"
        :x2="bx2"
        :y2="lty - 3"
        :stroke="dimLine('bridge')"
        stroke-width="1"
        stroke-dasharray="3 3"
      />
      <line
        :x1="bx1"
        :y1="annTopY"
        :x2="bx2"
        :y2="annTopY"
        :stroke="bright('bridge')"
        stroke-width="1.6"
        marker-start="url(#at)"
        marker-end="url(#ah)"
      />
      <text
        :x="CX"
        :y="annTopY - 11"
        text-anchor="middle"
        font-size="9.5"
        font-weight="600"
        :fill="bright('bridge')"
        letter-spacing="1.3"
        font-family="HE Manrope, Arial, sans-serif"
      >
        BRIDGE
      </text>
      <text
        :x="CX"
        :y="annTopY - 24"
        text-anchor="middle"
        font-size="13"
        font-weight="700"
        fill="rgba(220,185,90,1)"
        letter-spacing="0.5"
        font-family="HE Manrope, Arial, sans-serif"
      >
        {{ r(vals.bridge) }} mm
      </text>
    </g>

    <!-- FRAME WIDTH annotation -->
    <g
      @mouseenter="$emit('label-hover', 'frameW')"
      @mouseleave="$emit('label-hover', null)"
      style="cursor: default"
    >
      <line
        :x1="llx"
        :y1="annBotY - 8"
        :x2="llx"
        :y2="lby + 3"
        :stroke="dimLine('frameW')"
        stroke-width="1"
        stroke-dasharray="3 3"
      />
      <line
        :x1="rlx + wp"
        :y1="annBotY - 8"
        :x2="rlx + wp"
        :y2="lby + 3"
        :stroke="dimLine('frameW')"
        stroke-width="1"
        stroke-dasharray="3 3"
      />
      <line
        :x1="llx"
        :y1="annBotY"
        :x2="rlx + wp"
        :y2="annBotY"
        :stroke="bright('frameW')"
        stroke-width="1.6"
        marker-start="url(#at)"
        marker-end="url(#ah)"
      />
      <text
        :x="CX"
        :y="annBotY + 18"
        text-anchor="middle"
        font-size="9.5"
        font-weight="600"
        :fill="bright('frameW')"
        letter-spacing="1.3"
        font-family="HE Manrope, Arial, sans-serif"
      >
        FRAME WIDTH — {{ r(vals.lensW * 2 + vals.bridge) }} mm
      </text>
    </g>

    <!-- LENS HEIGHT annotation -->
    <g
      @mouseenter="$emit('label-hover', 'lensH')"
      @mouseleave="$emit('label-hover', null)"
      style="cursor: default"
    >
      <line
        :x1="annLeftX + 12"
        :y1="lty"
        :x2="llx - 3"
        :y2="lty"
        :stroke="dimLine('lensH')"
        stroke-width="1"
        stroke-dasharray="3 3"
      />
      <line
        :x1="annLeftX + 12"
        :y1="lby"
        :x2="llx - 3"
        :y2="lby"
        :stroke="dimLine('lensH')"
        stroke-width="1"
        stroke-dasharray="3 3"
      />
      <line
        :x1="annLeftX"
        :y1="lty"
        :x2="annLeftX"
        :y2="lby"
        :stroke="bright('lensH')"
        stroke-width="1.6"
        marker-start="url(#aup)"
        marker-end="url(#adn)"
      />
      <text
        :x="annLeftX - 14"
        :y="CY + 4"
        text-anchor="middle"
        font-size="9.5"
        font-weight="600"
        :fill="bright('lensH')"
        letter-spacing="1.3"
        font-family="HE Manrope, Arial, sans-serif"
        :transform="`rotate(-90,${annLeftX - 14},${CY})`"
      >
        LENS HEIGHT
      </text>
      <text
        :x="annLeftX - 27"
        :y="CY + 4"
        text-anchor="middle"
        font-size="13"
        font-weight="700"
        fill="rgba(220,185,90,1)"
        letter-spacing="0.5"
        font-family="HE Manrope, Arial, sans-serif"
        :transform="`rotate(-90,${annLeftX - 27},${CY})`"
      >
        {{ r(vals.lensH) }} mm
      </text>
    </g>
  </svg>
</template>

<script>
export default {
  name: "GlassesFront",
  props: {
    vals: { type: Object, required: true },
    activeLabel: { type: String, default: null },
  },
  emits: ["label-hover"],
  data() {
    return {
      S: 3.9,
      VW: 720,
      VH: 320,
      fw: 9.5,
      ac: "rgba(200,162,74,0.9)",
      dim: "rgba(190,155,74,0.38)",
    };
  },
  computed: {
    CX() {
      return this.VW / 2;
    },
    CY() {
      return this.VH / 2 + 18;
    },
    wp() {
      return this.vals.lensW * this.S;
    },
    hp() {
      return this.vals.lensH * this.S;
    },
    bp() {
      return this.vals.bridge * this.S;
    },
    rx() {
      return this.wp * 0.28;
    },
    llx() {
      return this.CX - this.bp / 2 - this.wp;
    },
    rlx() {
      return this.CX + this.bp / 2;
    },
    lty() {
      return this.CY - this.hp / 2;
    },
    lby() {
      return this.CY + this.hp / 2;
    },
    hingeY() {
      return this.lty + this.hp * 0.18;
    },
    annTopY() {
      return this.lty - 36;
    },
    annBotY() {
      return this.lby + 28;
    },
    annLeftX() {
      return this.llx - 38;
    },
    ltip() {
      return this.llx - 48;
    },
    rtip() {
      return this.rlx + this.wp + 48;
    },
    bx1() {
      return this.llx + this.wp;
    },
    bx2() {
      return this.rlx;
    },
    bridgeMidY() {
      return this.lty + this.hp * 0.28;
    },
    bridgeArchTopY() {
      return this.lty + this.hp * 0.05;
    },
    bridgeWallW() {
      return this.fw * 0.9;
    },
    bridgeOuterPath() {
      return `M ${this.bx1} ${this.bridgeMidY} Q ${this.CX} ${this.bridgeArchTopY} ${this.bx2} ${this.bridgeMidY}`;
    },
    bridgeInnerPath() {
      const w = this.bridgeWallW;
      return `M ${this.bx2} ${this.bridgeMidY + w} Q ${this.CX} ${
        this.bridgeArchTopY + w * 1.4
      } ${this.bx1} ${this.bridgeMidY + w}`;
    },
    bridgeFullPath() {
      const w = this.bridgeWallW;
      return `${this.bridgeOuterPath} L ${this.bx2} ${this.bridgeMidY + w} Q ${
        this.CX
      } ${this.bridgeArchTopY + w * 1.4} ${this.bx1} ${this.bridgeMidY + w} Z`;
    },
  },
  methods: {
    r(v) {
      return Math.round(v);
    },
    bright(id) {
      return this.activeLabel === id ? "rgba(220,185,90,1)" : this.ac;
    },
    dimLine(id) {
      return this.activeLabel === id ? "rgba(210,175,80,0.7)" : this.dim;
    },
  },
};
</script>
